/**
 * AliIDaaS CIAM OIDC for "TiDB Account" (provider name remains tidbcloud).
 * Token exchange: POST /idp/aliidaas/token; optional `source` (e.g. clinic) → account 选对应换码配置。
 */
import { getRedirectURL } from './auth'

const STATE_KEY = 'ciam.tidb.state'
const CODE_VERIFIER_KEY = 'ciam.tidb.code_verifier'

function generateRandomString(length: number): string {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'
  const array = new Uint8Array(length)
  crypto.getRandomValues(array)
  return Array.from(array, (byte) => chars[byte % chars.length]).join('')
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const data = new TextEncoder().encode(plain)
  return crypto.subtle.digest('SHA-256', data)
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  return base64UrlEncode(await sha256(verifier))
}

function normalize(v?: string): string {
  const t = (v || '').trim()
  if (!t || t.toLowerCase() === 'null' || t.toLowerCase() === 'undefined')
    return ''
  return t
}

export interface CiamTidbAuthConfig {
  issuer: string
  clientId: string
  scope: string
  redirectUri: string
  loginDomain?: string
  authorizationEndpoint?: string
  tokenProxyEndpoint?: string
  /** 非空则 JSON body 带 `source`，account 用 aliidaas.clinic 等槽位 */
  tokenProxySource?: string
}

export class CiamTidbAuthClient {
  private issuer: string
  private clientId: string
  private scope: string
  private redirectUri: string
  private loginDomain: string
  private authorizationEndpoint: string
  private tokenProxyEndpoint: string
  private tokenProxySource: string

  constructor(cfg: CiamTidbAuthConfig) {
    this.issuer = normalize(cfg.issuer).replace(/\/$/, '')
    this.clientId = normalize(cfg.clientId)
    this.scope = normalize(cfg.scope) || 'openid profile email'
    this.redirectUri = normalize(cfg.redirectUri) || getRedirectURL()
    this.loginDomain = normalize(cfg.loginDomain)
    this.authorizationEndpoint = normalize(cfg.authorizationEndpoint).replace(
      /\/$/,
      ''
    )
    this.tokenProxyEndpoint =
      normalize(cfg.tokenProxyEndpoint) || '/idp/aliidaas/token'
    this.tokenProxySource = normalize(cfg.tokenProxySource)
  }

  private getCiamBasePath(): string {
    const match = this.issuer.match(
      /^(https:\/\/[^/]+)\/api\/bff\/v1\.2\/developer\/ciam\/oidc\/[^/]+$/
    )
    return match ? `${match[1]}/api/bff/v1.2/developer/ciam` : ''
  }

  private getAuthorizationEndpoint(): string {
    if (this.authorizationEndpoint) return this.authorizationEndpoint
    const base = this.getCiamBasePath()
    if (base) return `${base}/oauth/authorize`
    if (this.loginDomain) {
      return `${this.loginDomain.replace(/\/$/, '')}/login/app/${
        this.clientId
      }/oauth2/authorize`
    }
    return `${this.issuer.replace(/\/oidc$/, '')}/oauth2/authorize`
  }

  async loginWithRedirect(): Promise<void> {
    const state = generateRandomString(32)
    const codeVerifier = generateRandomString(64)
    const codeChallenge = await generateCodeChallenge(codeVerifier)
    sessionStorage.setItem(STATE_KEY, state)
    sessionStorage.setItem(CODE_VERIFIER_KEY, codeVerifier)

    const authUrl = this.getAuthorizationEndpoint()
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      scope: this.scope,
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256'
    })
    window.location.href = `${authUrl}?${params.toString()}`
  }

  /**
   * Call after redirect: ?oauth_callback=1&code=&state=
   */
  async exchangeCodeForIdToken(): Promise<string> {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const state = params.get('state')
    const storedState = sessionStorage.getItem(STATE_KEY)
    const codeVerifier = sessionStorage.getItem(CODE_VERIFIER_KEY)
    sessionStorage.removeItem(STATE_KEY)
    sessionStorage.removeItem(CODE_VERIFIER_KEY)

    if (!code || !state) throw new Error('Missing code or state in callback')
    if (state !== storedState) throw new Error('OAuth state mismatch')
    if (!codeVerifier) throw new Error('Missing PKCE code_verifier')

    const url = this.tokenProxyEndpoint

    const body: Record<string, string> = {
      grant_type: 'authorization_code',
      code,
      redirect_uri: this.redirectUri,
      code_verifier: codeVerifier
    }
    if (this.tokenProxySource) {
      body.source = this.tokenProxySource
    }

    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
    if (!res.ok) {
      const text = await res.text()
      throw new Error(`Token exchange failed: ${text}`)
    }
    const data = (await res.json()) as { id_token?: string }
    const idToken = data.id_token || ''
    if (!idToken) throw new Error('No id_token in token response')
    return idToken
  }
}

let client: CiamTidbAuthClient | null = null

export function initCiamTidbAuth(cfg: CiamTidbAuthConfig): CiamTidbAuthClient {
  client = new CiamTidbAuthClient(cfg)
  return client
}

export function getCiamTidbAuth(): CiamTidbAuthClient | null {
  return client
}

export { STATE_KEY as CIAM_TIDB_STATE_KEY }
